<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import numberMixin from '../../../../mixins/numberMixin';
import ConfirmDialog from '../../../../components/TwConfirmDialog';
import salesTypeEnums from '../../../../variables/ski/school/sales-type-enums';
import statusEnums from '../../../../variables/ski/school/status-type-enums';

export default {
  name: 'SkiSchoolAvailableProducts',
  data() {
    return {
      selectedArea: null,
    };
  },
  components: {
    ConfirmDialog,
  },
  mixins: [numberMixin],
  computed: {
    ...mapGetters({
      participants: 'booking/getParticipantsWithFullName',
      availableDates: 'booking/getAvailableDates',
      mappedAvailableSchoolProducts: 'skischool/getAvailableSkiSchoolProducts2',
      availableSchoolProductsPersonCategoryHeaders: 'skischool/getAvailableSkiSchoolProductsPersonCategoryHeaders',
      defaultAreaId: 'booking/getDefaultAreaId',
      availableNumberOfDays: 'skischool/getAvailableNumberOfDays',
      availableWeekDays: 'skischool/getAvailableWeekDays',
      availableLessonDurations: 'skischool/getAvailableLessonDurations',
      availableNumberOfPersons: 'skischool/getAvailableNumberOfPersons',
      availableSkiTypes: 'skischool/getAvailableSkiTypes',
      availableLessonTypes: 'skischool/getAvailableLessonTypes',
      getPickupArea: 'skischool/getPickupArea',
    }),
    ...mapState({
      participantsLoading: state => state.booking.participants.itemsLoading,
      participantsError: state => state.booking.participants.itemsError,
      selectedBookingParticipantId: state => state.skischool.search.selectedBookingParticipantId,
      selectedNumberOfDays: state => state.skischool.search.selectedNumberOfDays,
      selectedLessonType: state => state.skischool.search.selectedLessonTypeId,
      selectedMultipleSkiTypes: state => state.skischool.search.selectedSkiTypes,
      selectedWeekDayNumber: state => state.skischool.search.selectedWeekDayNumber,
      selectedLessonDuration: state => state.skischool.search.selectedLessonDuration,
      selectedNumberOfPersons: state => state.skischool.search.selectedNumberOfPersons,
      pickupAreas: state => state.skischool.pickupAreas.items,
      bookingParticipants: state => state.skischool.bookingParticipants.items,
      skischools: state => state.skischool.schools.items,
      availableSchoolProducts: state => state.skischool.availableSchoolProducts,
      booking: state => state.booking.booking,
      user: state => state.app.auth.user,
      areas: state => state.booking.areas.items,
    }),
    canSearch() {
      return this.booking.id
        && this.booking.stays
        && this.booking.stays.length
        && this.participants
        && this.participants.length
        && this.selectedBookingParticipantId
        && (this.selectedArea || this.defaultAreaId);
    },
    loading() {
      return this.participantsLoading
        || this.skischools.itemsLoading
        || this.availableSchoolProducts.itemsLoading
        || this.availableSchoolProducts.saveLoading;
    },
    errorOccurred() {
      return this.skischools.itemsError
        || this.availableSchoolProducts.itemsError
        || this.participantsError
        || this.availableSchoolProducts.saveError;
    },
    areaParticipants() {
      return this.participants.filter(participant => participant.areaId === this.getSelectedAreaId());
    },
  },
  methods: {
    ...mapActions({
      loadAvailableSchoolPrices: 'skischool/availableSchoolPricesLoad',
      loadSchoolsByBookingId: 'skischool/schoolsByBookingIdLoad',
      createSchool: 'skischool/schoolCreate',
      deleteSchool: 'skischool/schoolDelete',
      setBookingParticipant: 'skischool/setBookingParticipant',
      setNumberOfDays: 'skischool/setNumberOfDays',
      setLessonType: 'skischool/setLessonType',
      setWeekDayNumber: 'skischool/setWeekDayNumber',
      setLessonDuration: 'skischool/setLessonDuration',
      setNumberOfPersons: 'skischool/setNumberOfPersons',
      setSkiTypes: 'skischool/setSkiTypes',
      setClearFilters: 'skischool/setClearFilters',
      loadSkiSchoolAreaPickupsBySeasonId: 'skischool/loadSkiSchoolAreaPickupsBySeasonId',
    }),
    createSkiSchoolProduct(product) {
      const priceGross = product.priceGross;
      const priceNet = product.priceNet;
      const personCategoryAges =
        `(${product.product.personCategory.ageFrom}-${product.product.personCategory.ageTo} years)`;
      let startDateTime = null;
      const targetWeekDay = product.product.weekDay;
      const areaId = this.getSelectedAreaId();
      const bookingStay = this.getBookingStayByAreaId(areaId);

      const arrivalDate = this.$moment(bookingStay.arrivalDate);
      const departureDate = this.$moment(bookingStay.departureDate);
      const daysDifference = departureDate.diff(arrivalDate, 'days');

      for (let i = 0; i <= daysDifference; i++) {
        const currentDate = this.$moment(arrivalDate).add(i, 'days');
        if (targetWeekDay === currentDate.day()) {
          startDateTime = `${currentDate.format('YYYY-MM-DD')} 00:00:00`;
          break;
        }
      }

      const skiSchool = {
        bookingParticipantId: this.selectedBookingParticipantId,
        productsSkiSchoolSeasonId: product.product.season.id,
        productsSkiSchoolSeasonPersonCategoryId: product.product.personCategory.id,
        productsSkiSchoolSeasonLevelId: product.product.level.id,
        productsSkiSchoolSkiTypeId: product.product.skiType.id,
        productsSkiSchoolLessonTypeId: product.product.lessonType.id,
        areaPickupId: product.product.areaPickupId,
        status: statusEnums.INITIATED,
        salesType: salesTypeEnums.BOOKING,
        supplierRef: null,
        supplierComment: null,
        customerComment: null,
        weekDay: targetWeekDay,
        startDateTime,
        maxNumberOfPersons: product.product.numberOfPersons,
        numberOfDays: product.product.days,
        minutes: product.product.durationMinutes,
        price: this.$options.filters.roundNumber(priceGross, 0),
        priceNet: this.$options.filters.roundNumber(priceNet, 0),
        currencyNet: product.product.season.supplier.currency,
        supplierName: product.product.season.supplier.name,
        seasonName: product.product.season.name,
        personCategoryName: product.product.personCategory.name + personCategoryAges,
        levelName: product.product.level.name,
        skiTypeName: product.product.skiType.name,
        lessonTypeName: product.product.lessonType.name,
        meetingPointName: null,
        seasonNameSupplier: product.product.season.name,
        personCategoryNameSupplier: product.product.personCategory.externalName + personCategoryAges,
        levelNameSupplier: product.product.level.externalName,
        skiTypeNameSupplier: product.product.skiType.identifier,
        lessonTypeNameSupplier: product.product.lessonType.identifier,
        meetingPointNameSupplier: null,
        areaId,
      };

      this.createSchool({
        bookingId: this.booking.id,
        bookingParticipantId: this.selectedBookingParticipantId,
        skiSchool,
      });
    },
    searchAvailableProducts() {
      if (!this.canSearch) {
        return;
      }
      const participant = this.participants.find(
        item => item.id === this.selectedBookingParticipantId,
      );
      const areaId = this.getSelectedAreaId();
      const bookingStay = this.getBookingStayByAreaId(areaId);
      this.loadAvailableSchoolPrices({
        areaId,
        participantAge: participant.age,
        arrivalDate: bookingStay.arrivalDate,
        departureDate: bookingStay.departureDate,
        targetCurrency: this.booking.currency,
        onlineCheck: true,
      });
    },
    getBookingStayByAreaId(areaId) {
      return this.booking.stays.find(stay => stay.areaId === areaId);
    },
    getSelectedAreaId() {
      return this.selectedArea || this.defaultAreaId;
    },
    doRefresh() {
      this.loadSchoolsByBookingId(this.booking.id);
      this.searchAvailableProducts();
    },
    deleteProductDialog(skiSchoolId) {
      this.deleteSchool({ skiSchoolId, salesAgentUserId: this.user.id });
    },
    openConfirmDialog(type) {
      const vueComponent = this.$refs[type];
      if (vueComponent instanceof Array) {
        vueComponent[0].open();
      } else {
        vueComponent.open();
      }
    },
    onBookingParticipantChange(participantId) {
      this.setBookingParticipant(participantId);
      this.searchAvailableProducts();
    },
    onAreaChange(areaId) {
      this.selectedArea = areaId;
      this.searchAvailableProducts();
    },
  },
};
</script>

<template>
  <v-container v-if="booking && booking.id" class="pa-0 pt-3" grid-list-xl>
    <v-card class="pa-3 mx-auto" outlined>
      <v-layout class="py-3 px-3">
        <v-flex class="ma-0 pa-0" sm9 xs12>
          <v-select
            @change="onBookingParticipantChange($event)"
            :value="selectedBookingParticipantId"
            :items="areaParticipants"
            :loading="participantsLoading"
            :disabled="participantsLoading || !participants.length"
            :label="$t('ski.school.selects.booking-participant')"
            item-text="fullName"
            item-value="id"
          />
        </v-flex>
        <v-flex v-if="areas.length > 1" class="pl-2 ma-0 pa-0" sm2 xs12>
          <v-select
            @change="onAreaChange($event)"
            :value="defaultAreaId"
            :items="areas"
            :label="$t('ski.school.selects.area')"
            item-text="name"
            item-value="id"
          />
        </v-flex>
        <v-flex class="pl-2 ma-0 pa-0" sm1 xs12>
          <v-btn
            @click="doRefresh"
            :loading="loading"
            icon
          >
            <v-icon>cached</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
      <v-divider></v-divider>
      <v-layout class="px-3 pt-3">
        <h3 class="d-inline-block">
          {{ $t('ski.school.filters') }}
        <v-btn
          @click="setClearFilters"
          class="mb-2"
          color="orange"
          flat
          small
        >
          {{ $t('ski.school.clear') }}
        </v-btn>
        </h3>
      </v-layout>
      <v-layout class="pl-3 pr-3" wrap>
        <v-flex class="ma-0 pa-0" sm5 xs12>
          <v-select
            @change="setLessonType($event)"
            :value="selectedLessonType"
            :items="Object.values(availableLessonTypes)"
            :loading="loading"
            :disabled="!Object.values(availableLessonTypes).length"
            label="Select lesson type"
            item-text="name"
            item-value="id"
            clearable
          />
        </v-flex>
        <v-flex class="pl-2 ma-0 pa-0" sm3 xs12>
          <v-select
            @change="setWeekDayNumber($event)"
            :value="selectedWeekDayNumber"
            :items="availableWeekDays"
            :loading="loading"
            :disabled="!availableWeekDays.length"
            :label="$t('ski.school.selects.week-day')"
            item-text="name"
            item-value="id"
            clearable
          />
        </v-flex>
        <v-flex class="pl-2 ma-0 pa-0" sm3 xs12>
          <v-select
            @change="setNumberOfDays($event)"
            :value="selectedNumberOfDays"
            :items="availableNumberOfDays"
            :loading="loading"
            :disabled="!availableNumberOfDays.length"
            :label="$t('ski.school.selects.number-of-days')"
            clearable
          />
        </v-flex>
      </v-layout>
      <v-layout class="pl-3 pr-3" wrap>
        <v-flex class="pl-2 ma-0 pa-0" sm3 xs12>
          <v-select
            @change="setLessonDuration($event)"
            :value="selectedLessonDuration"
            :items="availableLessonDurations"
            :loading="loading"
            :disabled="!availableLessonDurations.length"
            :label="$t('ski.school.selects.lesson-duration')"
            item-text="name"
            item-value="id"
            clearable
          />
        </v-flex>
        <v-flex class="pl-2 ma-0 pa-0" sm5 xs12>
          <v-autocomplete
            @change="setSkiTypes($event)"
            :value="selectedMultipleSkiTypes"
            :items="availableSkiTypes"
            :loading="loading"
            :disabled="!availableSkiTypes.length"
            :deletable-chips="true"
            :label="$t('ski.school.selects.multiple-ski-types')"
            item-text="name"
            item-value="id"
            clearable
            chips
            multiple
          />
        </v-flex>
        <v-flex class="pl-2 ma-0 pa-0" sm3 xs12>
          <v-select
            @change="setNumberOfPersons($event)"
            :value="selectedNumberOfPersons"
            :items="availableNumberOfPersons"
            :loading="loading"
            :disabled="!availableNumberOfPersons.length"
            :label="$t('ski.school.selects.number-of-persons')"
            clearable
          />
        </v-flex>
      </v-layout>
    </v-card>
    <div class="text-center pa-1">
      <v-progress-linear v-show="loading" :indeterminate="true"/>
    </div>
    <v-alert
      :value="this.errorOccurred"
      color="error"
      icon="warning"
      outline
    >
      <span v-html="errorOccurred"></span>
    </v-alert>
    <div
      v-if="!Object.keys(mappedAvailableSchoolProducts).length && canSearch && !loading"
      class="py-3 font-weight-bold"
    >
      {{ $t('ski.school.no-available-school-products') }}
    </div>

    <v-container v-if="Object.keys(mappedAvailableSchoolProducts).length > 0" grid-list-md>
      <div v-for="(skiLessonItem, skiLessonKey) in mappedAvailableSchoolProducts"
           :key="skiLessonKey"
           class="py-3 font-weight-bold">
        <h2>
          {{ skiLessonItem.lessonType.name }}
          <span class="font-weight-regular">({{ skiLessonItem.skiType.name }})</span>
        </h2>
        <div
            v-for="(group, groupKey) in skiLessonItem.items"
            :key="`group-${groupKey}`"
            class="ski-school-row-wrapper"
        >
          <div
            v-for="(level, levelKey) in group.levels"
            :key="'lesson'+skiLessonKey+'-gk-'+groupKey+'-lk-'+levelKey"
            class="ski-school-row card mb-4"
          >
            <div class="ski-school-row__info">
              <div class="pl-4">
                <v-icon title="level">hdr_weak</v-icon> {{ level.name }}<br>
                <v-icon title="persons">people</v-icon>
                {{ group.numberOfPersons }} persons
                <br>
                <v-icon title="days">calendar_today</v-icon>
                {{ group.days }} days (Start day: {{ group.weekDay }})<br>
                <v-icon title="minutes">schedule</v-icon>
                {{ group.durationMinutes }}
                {{ $t('ski.school.minutes-per-lesson') }}
              </div>
            </div>

            <div class="ski-school-row__price">
              <template v-if="Object.values(level.products).length">
                {{ level.products[0].priceGross }} {{ booking.currency }}
              </template>
            </div>

            <div class="ski-school-row__pickup">
              <v-flex
                v-for="(product, productKey) in level.products"
                :key="'lesson'+skiLessonKey+'-gk-'+groupKey+'-lk-'+levelKey+'-'+productKey"
                class="ma-0 pa-0"
                sm4
                xs12
              >
                <v-btn
                  v-if="product.product.id"
                  @click="createSkiSchoolProduct(product)"
                  :disabled="loading"
                  :loading="loading"
                  title="Add product"
                  outline
                  style="height: fit-content"
                  :style="{'background-color': product.product.availability === null ?
                    'lightgray !important' : (!product.product.availability ?
                    'lightred !important' : 'lightgreen !important')}"
                >
                  {{ product.product.time }} <br> {{ getPickupArea(product.product.areaPickupId) }}
                </v-btn>
                <!--
                 disabled = "|| product.product.availability === false"
                 -->

                <template v-if="product.bookingSchoolProductId">
                  (Already added)
                </template>
              </v-flex>
            </div>
          </div>
        </div>
      </div>
      <v-divider></v-divider>
    </v-container>
  </v-container>
</template>

<style lang="scss">
  .ski-school-row-wrapper {
    display: flex;
    flex-direction: column;
  }

  .ski-school-row {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__info {
      width: 35%;
    }

    &__price {
      width: 10%;
    }

    &__pickup {
      width: 50%;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/booking/ski/school/SkiSchoolAvailableProducts.vue